import { useContext, useEffect, useMemo, useState } from 'react'
import {
  BasketContext,
  isConsultationBasketItem,
  isShippingTestBasketItem,
  isTestBasketItem,
  isTrainingBasketItem,
} from '../provider/basket'
import { AuthContext } from '../provider/auth'
import {
  useGetOrderCostQuery,
  GetAvailableTestDocument,
  GetAvailableTestQuery,
} from '../generated/urql.anonymous'
import {
  ClientProfileFragment,
  useVerifyKdrMutation,
} from '../generated/urql.client'
import { uniqBy } from 'lodash'
import {
  isProfileGenderComplete,
  isProfileIdentityComplete,
} from '../lib/profile'
import { useFormContext } from 'react-hook-form'
import { BasketForm } from '../pages/basket/NewBasket'
import { useClient } from 'urql'

const isProfile = (
  profile: ClientProfileFragment | undefined
): profile is ClientProfileFragment =>
  !!profile &&
  (!isProfileIdentityComplete(profile) || !isProfileGenderComplete(profile))

const useBasketData = () => {
  const basket = useContext(BasketContext)
  const auth = useContext(AuthContext)
  const form = useFormContext<BasketForm>()
  const client = useClient()

  const collectionDate = form.watch('collectionDate')
  const collectionType = form.watch('collectionType') || 'home'
  const collectionPointId = form.watch('collectionPointId')
  const collectionSlotId = form.watch('collectionSlotId')
  const isVoucher = form.watch('isVoucher')
  const selectedCollectionPoint = form.watch('selectedCollectionPoint')

  const {
    items,
    address,
    discountCodes,
    profile,
    shippingData,
    changeItem,
    removeItem,
    setDiscountCodes,
    clear,
    setShippingData,
  } = basket

  const [testsAvailability, setTestsAvailability] = useState<
    Map<number, { isNurseAvailable?: boolean | null }>
  >(new Map())

  const rawTests = useMemo(
    () => items.filter(isTestBasketItem),
    [items]
  )

  // Stabilna reprezentacja lokalizacji dla zależności
  const locationKey = useMemo(
    () =>
      address?.location?.coordinates
        ? JSON.stringify(address.location.coordinates)
        : null,
    [address?.location?.coordinates]
  )

  // Stabilna reprezentacja ID testów dla zależności
  const testIdsKey = useMemo(
    () =>
      rawTests
        .map((item) => item.test.id)
        .sort((a, b) => a - b)
        .join(','),
    [rawTests]
  )

  // Pobierz aktualne dane testów na podstawie aktualnej lokalizacji
  useEffect(() => {
    if (!address?.location || rawTests.length === 0) {
      setTestsAvailability(new Map())
      return
    }

    let cancelled = false
    const testIds = rawTests.map((item) => item.test.id)

    // Pobierz dane dla każdego testu
    Promise.all(
      testIds.map((testId) =>
        client
          .query<GetAvailableTestQuery>(
            GetAvailableTestDocument,
            {
              id: testId,
              point: address.location,
            },
            { requestPolicy: 'network-only' }
          )
          .toPromise()
          .then(({ data }) => {
            if (cancelled) return null
            return {
              testId,
              isNurseAvailable: data?.test_by_pk?.isNurseAvailable,
            }
          })
          .catch(() => {
            if (cancelled) return null
            return { testId, isNurseAvailable: null }
          })
      )
    ).then((results) => {
      if (!cancelled && results) {
        const newMap = new Map<
          number,
          { isNurseAvailable?: boolean | null }
        >()
        results.forEach((result) => {
          if (result) {
            newMap.set(result.testId, {
              isNurseAvailable: result.isNurseAvailable,
            })
          }
        })
        setTestsAvailability(newMap)
      }
    })

    return () => {
      cancelled = true
    }
  }, [locationKey, testIdsKey, client])

  const tests = useMemo(
    () =>
      rawTests.map((item) => ({
        ...item,
        test: {
          ...item.test,
          isNurseAvailable:
            testsAvailability.get(item.test.id)?.isNurseAvailable ??
            item.test.isNurseAvailable,
        },
      })),
    [rawTests, testsAvailability]
  )

  const shippingTests = items.filter(isShippingTestBasketItem)
  const consultations = items.filter(isConsultationBasketItem)
  const trainings = items.filter(isTrainingBasketItem)

  const needsShippingToClient = useMemo(
    () => !!shippingTests.find((t) => !!t.shouldShipToClient),
    [shippingTests]
  )

  const variables = useMemo(
    () => ({
      consultations: consultations.map(
        ({ consultation, preferredDate, consultationSlot, profile }) => ({
          consultationId: consultation.id,
          preferredDate: consultationSlot?.dateFrom
            ? new Date(consultationSlot?.dateFrom).toISOString()
            : preferredDate,
          profile: {
            firstName: profile?.firstName || '-',
            lastName: profile?.lastName || '-',
            identity: profile?.identity || '-',
            gender: profile?.gender || '-',
          },
          consultationSlotId: consultationSlot?.id,
        })
      ),

      trainings: trainings.map(({ training }) => ({
        trainingId: training.id,
      })),

      collections: tests.length
        ? [
            {
              location:
                collectionType === 'stationary' && selectedCollectionPoint
                  ? selectedCollectionPoint.location.coordinates
                  : basket.address?.location?.coordinates || [0, 0],
              tests: tests.map(({ test, profile }) => ({
                testId: test.id,
                profile: {
                  firstName: profile?.firstName || '-',
                  lastName: profile?.lastName || '-',
                  identity: profile?.identity || '-',
                  gender: profile?.gender || '-',
                },
              })),
              collectionDate:
                collectionType === 'home' && collectionDate
                  ? new Date(collectionDate).toISOString()
                  : undefined,
              collectionPointId:
                collectionType === 'stationary' ? collectionPointId : undefined,
              collectionSlotId:
                collectionType === 'stationary' && !isVoucher
                  ? collectionSlotId
                  : undefined,
              isVoucher:
                collectionType === 'stationary' ? isVoucher || false : undefined,
            },
          ]
        : [],

      shipping: !!shippingTests.length
        ? {
            providerCode: shippingData?.provider,
            tests: shippingTests.map(
              ({ test, profile, shouldShipToClient }) => ({
                testId: test.id,
                profile: {
                  firstName: profile?.firstName || '-',
                  lastName: profile?.lastName || '-',
                  identity: profile?.identity || '-',
                  gender: profile?.gender || '-',
                },
                shouldShipToClient,
              })
            ),
          }
        : undefined,

      discountCodes: basket.discountCodes,
    }),
    [
      basket.items,
      basket.discountCodes,
      basket.address,
      collectionDate,
      collectionType,
      collectionPointId,
      collectionSlotId,
      isVoucher,
      selectedCollectionPoint,
      shippingData?.provider,
    ]
  )

  const [
    { data: orderCostData, error: orderCostError, fetching: orderCostFetching },
  ] = useGetOrderCostQuery({
    variables,
    pause: !auth.accessToken,
  })
  const [, verifyKDR] = useVerifyKdrMutation()

  const orderCost = orderCostData?.client_order_cost

  const incompleteProfiles = useMemo(
    () =>
      uniqBy(
        [...tests, ...shippingTests, ...consultations]
          .map((item) => item.profile)
          .filter(isProfile),
        'id'
      ),
    [tests, shippingTests, consultations]
  )

  return {
    orderCost,
    orderCostError,
    orderCostFetching,
    isEmpty: !items.length,
    tests,
    shippingTests,
    consultations,
    trainings,
    address,
    discountCodes,
    profile,
    incompleteProfiles,
    shippingData,
    needsShippingToClient,
    form,
    changeItem,
    removeItem,
    setDiscountCodes,
    verifyKDR,
    clear,
    setShippingData,
  }
}

export default useBasketData
